// src/components/main/produzione/assegnazioniGantt/GanttBar.tsx

import React, {useMemo, useRef, useState} from "react";
import { useDrag } from "react-dnd";
import { DateTime } from "luxon";

import { AssegnazioneGantt } from "./assegnazioniGanttTypes";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import ConfermaAnnulla from "../../../../../../common/ConfermaAnnulla";

// ============================================
// CONSTANTS
// ============================================

export const GANTT_BAR_TYPE_PREFIX = "GANTT_BAR_TECNICA_";
export const GANTT_RESIZE_LEFT_PREFIX = "GANTT_RESIZE_LEFT_TECNICA_";
export const GANTT_RESIZE_RIGHT_PREFIX = "GANTT_RESIZE_RIGHT_TECNICA_";
export const GANTT_OL_TYPE_PREFIX = "GANTT_OL_TECNICA_";

/**
 * Genera il type per drag barra basato sulla tecnica
 */
export const getGanttBarType = (tecnicaId: number | undefined): string => {
    return `${GANTT_BAR_TYPE_PREFIX}${tecnicaId || 0}`;
};

export const getResizeLeftType = (tecnicaId: number | undefined): string => {
    return `${GANTT_RESIZE_LEFT_PREFIX}${tecnicaId || 0}`;
};

export const getResizeRightType = (tecnicaId: number | undefined): string => {
    return `${GANTT_RESIZE_RIGHT_PREFIX}${tecnicaId || 0}`;
};

/**
 * Genera il type per drag OdL basato sulla tecnica
 */
export const getGanttOLType = (tecnicaId: number | undefined): string => {
    return `${GANTT_OL_TYPE_PREFIX}${tecnicaId || 0}`;
};

/**
 * Genera array di type accettati basato sulle capacità della macchina
 * Include: barre, resize left/right, ordini di lavoro
 */
export const getAcceptedTypes = (tecniche?: number[] | undefined): string[] => {
    if (!tecniche || tecniche.length === 0) {
        return [];
    }
    const types: string[] = [];
    tecniche.forEach((tecnica) => {
        types.push(getGanttBarType(tecnica));
        types.push(getResizeLeftType(tecnica));
        types.push(getResizeRightType(tecnica));
        types.push(getGanttOLType(tecnica));
    });
    return types;
};

// ============================================
// INTERFACES
// ============================================

interface GanttBarProps {
    assegnazione: AssegnazioneGantt;
    dataInizio: DateTime;
    totaleGiorni: number;
    lane: number;
    isSelected: boolean;
    onClick: (ordineLavoroId: number) => void;

    onAssegnazioneDelete: (
        assegnazioneId: number,
    ) => void;
}

export interface GanttBarDragItem {
    type: string;
    assegnazioneId: number;
    ordineLavoroId: number;
    macchinaId: number;
    tecnicaId: number | undefined;
    dataDa: string;
    dataA: string;
    resizeEdge?: "left" | "right";
}

export interface GanttOLDragItem {
    type: string;
    ordineLavoroId: number;
    tecnicaId: number | undefined;
    valoreConfermato: number | undefined;
    // Campi per drag preview
    codiceItem?: string;
    tecnicaDisplay?: string;
}

// ============================================
// SUB-COMPONENTS
// ============================================

interface ResizeHandleProps {
    type: string;
    item: GanttBarDragItem;
    edge: "left" | "right";
}

const ResizeHandle = ({ type, item, edge }: ResizeHandleProps) => {
    const ref = useRef<HTMLDivElement>(null);

    const [{ isDragging }, drag] = useDrag(
        () => ({
            type,
            item: { ...item, resizeEdge: edge },
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [type, item, edge]
    );

    drag(ref);

    return (
        <Box
            ref={ref}
            sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                width: 8,
                [edge]: 0,
                cursor: "ew-resize",
                backgroundColor: isDragging ? "rgba(255,255,255,0.5)" : "transparent",
                "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.3)",
                },
                zIndex: 1,
            }}
        />
    );
};

// ============================================
// COMPONENT
// ============================================

const GanttBar = ({
                      assegnazione,
                      dataInizio,
                      totaleGiorni,
                      lane,
                      isSelected,
                      onClick,
                      onAssegnazioneDelete
                  }: GanttBarProps) => {
    const theme = useTheme();
    const ref = useRef<HTMLDivElement>(null);

    // Types basati sulla tecnica
    const dragType = useMemo(
        () => getGanttBarType(assegnazione.tecnica),
        [assegnazione.tecnica]
    );

    const resizeLeftType = useMemo(
        () => getResizeLeftType(assegnazione.tecnica),
        [assegnazione.tecnica]
    );

    const resizeRightType = useMemo(
        () => getResizeRightType(assegnazione.tecnica),
        [assegnazione.tecnica]
    );

    // Item base per drag
    const dragItem: GanttBarDragItem = useMemo(
        () => ({
            type: dragType,
            assegnazioneId: assegnazione.id!,
            ordineLavoroId: assegnazione.ordine_di_lavoro!,
            macchinaId: assegnazione.macchina!,
            tecnicaId: assegnazione.tecnica,
            dataDa: assegnazione.data_da as string,
            dataA: assegnazione.data_a as string,
        }),
        [assegnazione, dragType]
    );

    // Calcola posizione e larghezza
    const { left, width, isVisible } = useMemo(() => {
        const assDataDa = DateTime.fromISO(assegnazione.data_da as string);
        const assDataA = DateTime.fromISO(assegnazione.data_a as string);
        // const dataFine = dataInizio.plus({ days: totaleGiorni });
        let totaleGiorniSenzaDomeniche = 0
        let dataFine = dataInizio;

        while (totaleGiorniSenzaDomeniche <= totaleGiorni) {
            if (dataFine.weekday !== 7) {
                totaleGiorniSenzaDomeniche++;
            }
            dataFine = dataFine.plus({ days: 1 });
        }

        // Clamp delle date al range visibile
        const dataInizioEffettiva = assDataDa < dataInizio ? dataInizio : assDataDa;
        const dataFineEffettiva = assDataA > dataFine ? dataFine : assDataA;

        // Se l'assegnazione è completamente fuori range, non mostrare
        if (assDataA < dataInizio || assDataDa > dataFine) {
            return { left: "0%", width: "0%", isVisible: false };
        }

        // Giorni dall'inizio della vista (escluse domeniche)
        let giorniDaInizio = 0;
        let giorno = dataInizio;
        while (giorno < dataInizioEffettiva) {
            if (giorno.weekday !== 7) {
                giorniDaInizio++;
            }
            giorno = giorno.plus({ days: 1 });
        }
        // Durata in giorni visibili (escluse domeniche)
        let durataGiorni = 0;
        giorno = dataInizioEffettiva;
        while (giorno <= dataFineEffettiva) {
            if (giorno.weekday !== 7) {
                durataGiorni++;
            }
            giorno = giorno.plus({ days: 1 });
        }

        const leftPercent = (giorniDaInizio / totaleGiorni) * 100;
        const widthPercent = (durataGiorni / totaleGiorni) * 100;


        return {
            left: `${leftPercent}%`,
            width: `${Math.max(widthPercent, 2)}%`,
            isVisible: true,
        };
    }, [assegnazione.data_da, assegnazione.data_a, dataInizio, totaleGiorni]);

    // Drag per spostamento intera barra
    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: dragType,
            item: dragItem,
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [dragType, dragItem]
    );

    drag(ref);

    // Non renderizzare se fuori range
    if (!isVisible) {
        return null;
    }

    // Tooltip content
    const tooltipContent = (
        <Box>
            <Typography variant="body2" fontWeight="bold">
                {assegnazione.codice_item || `#${assegnazione.id}`}
            </Typography>
            <Typography variant="caption" display="block">
                {assegnazione.descrizione_item}
            </Typography>
            <Typography variant="caption" display="block">
                Mandatario: {assegnazione.mandatario_display || "-"}
            </Typography>
            <Typography variant="caption" display="block">
                Tecnica: {assegnazione.tecnica_display || "-"}
            </Typography>
            <Typography variant="caption" display="block">
                {DateTime.fromISO(assegnazione.data_da as string).toFormat("dd/MM")} - {DateTime.fromISO(assegnazione.data_a as string).toFormat("dd/MM")}
            </Typography>
            {assegnazione.valore_schedulato && (
                <Typography variant="caption" display="block">
                    Valore: € {assegnazione.valore_schedulato.toLocaleString("it-IT")}
                </Typography>
            )}
            <Button variant="contained" sx={{mt:2}} color='warning' onClick={()=>onAssegnazioneDelete(assegnazione.id!)}>Elimina assegnazione</Button>
        </Box>
    );

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (assegnazione.ordine_di_lavoro) {
            onClick(assegnazione.ordine_di_lavoro);
        }
    };

    return (
        <Tooltip title={tooltipContent} arrow placement="top">
            <Box
                ref={ref}
                onClick={handleClick}
                sx={{
                    position: "absolute",
                    top: 8 + lane * 40,
                    left,
                    width,
                    height: 36,
                    backgroundColor: isSelected
                        ? theme.palette.primary.main
                        : theme.palette.info.main,
                    border: `2px solid ${
                        isSelected
                            ? theme.palette.primary.dark
                            : theme.palette.info.dark
                    }`,
                    borderRadius: 1,
                    cursor: "grab",
                    opacity: isDragging ? 0.5 : 1,
                    pointerEvents: "auto",
                    display: "flex",
                    alignItems: "center",
                    px: 1.5,
                    overflow: "hidden",
                    transition: "box-shadow 0.2s",
                    "&:hover": {
                        boxShadow: theme.shadows[4],
                    },
                }}
            >
                {/* Handle resize sinistro */}
                <ResizeHandle
                    type={resizeLeftType}
                    item={dragItem}
                    edge="left"
                />

                {/* Contenuto */}
                <Stack direction="row" spacing={2} sx={{}}>
                <Typography
                    variant="caption"
                    sx={{
                        color: "#fff",
                        fontWeight: "bold",
                    }}
                >
                    {assegnazione.codice_item || assegnazione.ordine_di_lavoro_display}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        color: "#ddd",
                    }}
                >
                    : €{assegnazione.valore_schedulato?.toLocaleString("it-IT")}
                </Typography>
                </Stack>

                {/* Handle resize destro */}
                <ResizeHandle
                    type={resizeRightType}
                    item={dragItem}
                    edge="right"
                />

            </Box>

        </Tooltip>
    );
};

export default GanttBar;